import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { translations } from "@/translations";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { PaperTrading } from "@/components/training/PaperTrading";
import { FlashCards } from "@/components/training/FlashCards";
import {
  Activity,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Minus,
  Zap,
  BarChart3,
  Target,
  Bell,
  BellRing,
  Trash2,
  Plus,
  X,
  GraduationCap,
  Info,
  ChevronDown,
  ChevronUp,
  LogOut,
  Shield,
  User,
  Timer,
  AlertTriangle,
  Languages,
  Search,
  Globe,
} from "lucide-react";
import { LoginPage } from "@/components/auth/LoginPage";
import { AdminPanel } from "@/components/admin/AdminPanel";
import { SignalCard, EducationCard, MethodSignalView } from "@/components/signals/SignalComponents";
import { EconomicCalendarWidget } from "@/components/signals/EconomicCalendar";
import { StrategyAnalysis } from "@/components/signals/StrategyAnalysis";
import { MorningStarPage } from "@/components/signals/TradingZonesChart";
import { KawaiiNav } from "@/components/navigation/KawaiiNav";
import { MembershipLock } from "@/components/auth/MembershipLock";
import { BasicTierView } from "@/components/auth/BasicTierView";
import { StrategiesListPage } from "@/components/strategies/StrategiesListPage";
import { StrategyDetailPage } from "@/components/strategies/StrategyDetailPage";
import { ElliottWavePage } from "@/components/strategies/ElliottWavePage";
import { FibonacciPage } from "@/components/strategies/FibonacciPage";
import { AdvancedPage } from "@/components/strategies/AdvancedPage";
import { TiaScalpingPage } from "@/components/strategies/TiaScalpingPage";
import { useInactivityLogout } from "@/lib/useInactivityLogout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAIRS = [
  // Forex - Major Pairs
  { symbol: "XAU/USD", name: "Gold", category: "Forex" },
  { symbol: "XAG/USD", name: "Silver", category: "Forex" },
  { symbol: "EUR/USD", name: "EUR/USD", category: "Forex" },
  { symbol: "GBP/USD", name: "GBP/USD", category: "Forex" },
  { symbol: "USD/JPY", name: "USD/JPY", category: "Forex" },
  { symbol: "USD/CHF", name: "USD/CHF", category: "Forex" },
  { symbol: "AUD/USD", name: "AUD/USD", category: "Forex" },
  { symbol: "NZD/USD", name: "NZD/USD", category: "Forex" },
  { symbol: "USD/CAD", name: "USD/CAD", category: "Forex" },
  // Forex - Cross Pairs
  { symbol: "EUR/GBP", name: "EUR/GBP", category: "Forex" },
  { symbol: "EUR/JPY", name: "EUR/JPY", category: "Forex" },
  { symbol: "GBP/JPY", name: "GBP/JPY", category: "Forex" },
  { symbol: "EUR/AUD", name: "EUR/AUD", category: "Forex" },
  { symbol: "GBP/AUD", name: "GBP/AUD", category: "Forex" },
  { symbol: "EUR/CAD", name: "EUR/CAD", category: "Forex" },
  { symbol: "AUD/JPY", name: "AUD/JPY", category: "Forex" },
  { symbol: "CHF/JPY", name: "CHF/JPY", category: "Forex" },
  // Indices
  { symbol: "NQ1!", name: "NASDAQ 100", category: "Indices" },
  { symbol: "YM1!", name: "Dow Jones", category: "Indices" },
  { symbol: "ES1!", name: "S&P 500", category: "Indices" },
  { symbol: "FTSE", name: "FTSE 100", category: "Indices" },
  { symbol: "DAX", name: "DAX 40", category: "Indices" },
  { symbol: "NI225", name: "Nikkei 225", category: "Indices" },
  // Commodities
  { symbol: "CC1!", name: "Cocoa", category: "Commodities" },
  { symbol: "CL1!", name: "Crude Oil", category: "Commodities" },
  { symbol: "SB1!", name: "Sugar", category: "Commodities" },
  { symbol: "KC1!", name: "Coffee", category: "Commodities" },
  { symbol: "CT1!", name: "Cotton", category: "Commodities" },
  { symbol: "NG1!", name: "Natural Gas", category: "Commodities" },
  { symbol: "HG1!", name: "Copper", category: "Commodities" },
  // Crypto
  { symbol: "BTC/USD", name: "Bitcoin", category: "Crypto" },
  { symbol: "ETH/USD", name: "Ethereum", category: "Crypto" },
  { symbol: "SOL/USD", name: "Solana", category: "Crypto" },
  { symbol: "DOGE/USD", name: "Dogecoin", category: "Crypto" },
  { symbol: "XRP/USD", name: "XRP", category: "Crypto" },
  { symbol: "BNB/USD", name: "BNB", category: "Crypto" },
  { symbol: "ADA/USD", name: "Cardano", category: "Crypto" },
  { symbol: "AVAX/USD", name: "Avalanche", category: "Crypto" },
  { symbol: "MATIC/USD", name: "Polygon", category: "Crypto" },
  { symbol: "DOT/USD", name: "Polkadot", category: "Crypto" },
];

const TIMEFRAMES = ["5min", "15min", "30min", "1h", "4h", "1day", "1w", "2w", "3w", "1mo"];

// Signal Methods/Indicators - each gives its own independent signal (19 indicators + news)
const SIGNAL_METHODS = [
  { value: "combined", label: "Combined (All)", labelAr: "مجمع (الكل)" },
  { value: "sma", label: "SMA", labelAr: "المتوسط البسيط SMA" },
  { value: "ema", label: "EMA", labelAr: "المتوسط الأسي EMA" },
  { value: "macd", label: "MACD", labelAr: "مؤشر MACD" },
  { value: "adx", label: "ADX (Trend)", labelAr: "قوة الاتجاه ADX" },
  { value: "rsi", label: "RSI", labelAr: "القوة النسبية RSI" },
  { value: "stochastic", label: "Stochastic", labelAr: "ستوكاستيك" },
  { value: "cci", label: "CCI", labelAr: "مؤشر قناة السلع CCI" },
  { value: "bollinger", label: "Bollinger Bands", labelAr: "نطاقات بولينجر" },
  { value: "atr", label: "ATR (Volatility)", labelAr: "متوسط المدى الحقيقي ATR" },
  { value: "obv", label: "OBV (Volume)", labelAr: "حجم التوازن OBV" },
  { value: "tick_volume", label: "Tick Volume", labelAr: "حجم التداول" },
  { value: "fibonacci", label: "Fibonacci", labelAr: "فيبوناتشي" },
  { value: "pivot_points", label: "Pivot Points", labelAr: "نقاط المحور" },
  { value: "trendlines", label: "Trendlines", labelAr: "خطوط الاتجاه" },
  { value: "support_resistance", label: "Support/Resistance", labelAr: "الدعوم والمقاومات" },
  { value: "candlestick_patterns", label: "Candlestick Patterns", labelAr: "أنماط الشموع" },
  { value: "ichimoku", label: "Ichimoku Cloud", labelAr: "سحابة إيشيموكو" },
  { value: "order_blocks", label: "Order Blocks (SMC)", labelAr: "بلوكات الأوامر (SMC)" },
  { value: "volume_profile", label: "Volume Profile", labelAr: "ملف الحجم" },
  { value: "news_sentiment", label: "News Sentiment", labelAr: "تحليل الأخبار" },
];

const ALERT_TYPES = [
  { value: "SIGNAL_BUY", label: "BUY Signals Only" },
  { value: "SIGNAL_SELL", label: "SELL Signals Only" },
  { value: "SIGNAL_ANY", label: "Any Signal (BUY or SELL)" },
  { value: "PRICE_ABOVE", label: "Price Above Target" },
  { value: "PRICE_BELOW", label: "Price Below Target" },
];

function App() {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showPaperTrading, setShowPaperTrading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Language state
  const [lang, setLang] = useState(() => {
    return localStorage.getItem("ttanalysis_lang") || "en";
  });
  const t = translations[lang];
  const isRTL = lang === "ar";

  // Trading state - restore symbol from localStorage
  const [symbol, setSymbol] = useState(() => localStorage.getItem("ttanalysis_symbol") || "XAU/USD");
  const [timeframe, setTimeframe] = useState(() => localStorage.getItem("ttanalysis_timeframe") || "5min");
  const [chartData, setChartData] = useState([]);
  const [indicators, setIndicators] = useState(null);
  const [levels, setLevels] = useState(null);
  const [signal, setSignal] = useState(null);
  const [bestBuy, setBestBuy] = useState(null);
  const [bestSell, setBestSell] = useState(null);
  const [loading, setLoading] = useState(false);
  const [price, setPrice] = useState(null);
  const [activeTab, setActiveTab] = useState("chart");
  const [signalTimeframe] = [timeframe];
  const [signalMethod, setSignalMethod] = useState("combined");
  const [showMorningStar, setShowMorningStar] = useState(false);
  const [activePage, setActivePage] = useState("signals");
  const [selectedStrategyId, setSelectedStrategyId] = useState(null);
  const [membershipData, setMembershipData] = useState(null);
  const [goldRateError, setGoldRateError] = useState(null);
  
  // Data loaded state - nothing fetches until user clicks Analyze
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Multi-Indicator Signals state
  const [multiIndicatorData, setMultiIndicatorData] = useState(null);
  const [multiIndicatorLoading, setMultiIndicatorLoading] = useState(false);
  
  // Alerts state
  const [subscriptions, setSubscriptions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [newAlert, setNewAlert] = useState({ 
    symbol: "XAU/USD", 
    alert_type: "SIGNAL_ANY", 
    price_target: "",
    timeframe: "5min",
    min_confidence: 50
  });
  
  // Signal stability info
  const [signalMeta, setSignalMeta] = useState(null);


  // Check auth on mount
  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    if (savedToken && savedUser) {
      // Verify token is still valid
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${savedToken}` }
      }).then(res => {
        setUser(res.data);
        setToken(savedToken);
        setIsAuthenticated(true);
        // Fetch membership status
        axios.get(`${API}/membership-status`, { headers: { Authorization: `Bearer ${savedToken}` } })
          .then(r => setMembershipData(r.data))
          .catch(() => {});
      }).catch(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      }).finally(() => {
        setCheckingAuth(false);
      });
    } else {
      setCheckingAuth(false);
    }
  }, []);

  const handleLogin = (userData, accessToken) => {
    setUser(userData);
    setToken(accessToken);
    setIsAuthenticated(true);
    // Fetch membership status
    axios.get(`${API}/membership-status`, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(res => setMembershipData(res.data))
      .catch(() => {});
  };

  const handleLogout = () => {
    // Notify backend so session duration is recorded
    if (token) {
      axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    setShowAdminPanel(false);
    setMembershipData(null);
  };

  const handlePayPalRenew = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/paypal-subscribe`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.paypal_url) {
        window.open(res.data.paypal_url, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      console.error(e);
    }
  }, [token]);

  // Auto-logout after 2 minutes of inactivity (everyone except admin)
  useInactivityLogout({
    enabled: isAuthenticated && user?.role !== "admin",
    onLogout: handleLogout,
  });

  // Tier gating: force Education-only users to strategies page only
  useEffect(() => {
    if (!user || user.role === "admin") return;
    const hasAdvanced = user.tier_advanced || user.signal_tier === "advanced";
    const hasBasic = user.tier_basic || user.signal_tier === "basic";
    // Education-only (no advanced, no basic) → lock to strategies tab
    if (!hasAdvanced && !hasBasic && activePage !== "strategies") {
      setActivePage("strategies");
    }
  }, [user, activePage]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const urlSymbol = symbol.replace("/", "-").replace("!", "");
      const res = await axios.get(`${API}/ohlc/${urlSymbol}`, {
        params: { interval: timeframe, outputsize: 100 }
      });
      if (res.data) {
        setChartData(res.data.data || []);
        setIndicators(res.data.indicators || null);
        setLevels(res.data.support_resistance || null);
        setSignal(res.data.signal || null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [symbol, timeframe]);

  // Toggle language
  const toggleLanguage = () => {
    const newLang = lang === "en" ? "ar" : "en";
    setLang(newLang);
    localStorage.setItem("ttanalysis_lang", newLang);
  };

  const fetchPrice = useCallback(async () => {
    try {
      const urlSymbol = symbol.replace("/", "-").replace("!", "");
      const res = await axios.get(`${API}/price/${urlSymbol}`);
      if (res.data && res.data.price > 0) setPrice(res.data.price);
    } catch (e) {
      console.error(e);
    }
  }, [symbol]);

  const fetchSignals = useCallback(async () => {
    try {
      const urlSymbol = symbol.replace("/", "-").replace("!", "");
      // Fetch dual signal (both BUY and SELL) for selected timeframe
      const res = await axios.get(`${API}/dual-signal/${urlSymbol}`, {
        params: { timeframe: signalTimeframe }
      });
      if (res.data) {
        setBestBuy(res.data.buy_signal || null);
        setBestSell(res.data.sell_signal || null);
        // Store signal metadata for stability info
        setSignalMeta({
          recommendation: res.data.recommendation,
          overall_trend: res.data.overall_trend,
          valid_until: res.data.signal_valid_until,
          cache_duration: res.data.cache_duration_seconds
        });
      }
    } catch (e) {
      console.error(e);
      // Fallback to old method
      try {
        const fallbackSymbol = symbol.replace("/", "-").replace("!", "");
        const res = await axios.get(`${API}/signals/${fallbackSymbol}`);
        if (res.data) {
          setBestBuy(res.data.best_buy || null);
          setBestSell(res.data.best_sell || null);
        }
      } catch (err) {
        console.error(err);
      }
    }
  }, [symbol, signalTimeframe]);

  // Fetch Multi-Indicator Signals
  const fetchMultiIndicatorSignals = useCallback(async () => {
    setMultiIndicatorLoading(true);
    try {
      const urlSymbol = symbol.replace("/", "-").replace("!", "");
      const res = await axios.get(`${API}/multi-indicator-signals/${urlSymbol}`, {
        params: { timeframe: signalTimeframe },
        headers: { Authorization: `Bearer ${token}` }
      });
      setMultiIndicatorData(res.data);
      // Use multi-indicator price as fallback if price didn't load
      if (res.data?.current_price && res.data.current_price > 0) {
        setPrice(prev => prev && prev > 0 ? prev : res.data.current_price);
      }
    } catch (e) {
      console.error("Error fetching multi-indicator signals:", e);
      // Handle per-user analyze rate limit (3 min)
      if (e.response?.status === 429) {
        const detail = e.response.data?.detail;
        const remaining = detail?.remaining_seconds || 180;
        const mins = Math.ceil(remaining / 60);
        setGoldRateError(`${remaining}s (~${mins}m)`);
        setTimeout(() => setGoldRateError(null), remaining * 1000);
      }
    } finally {
      setMultiIndicatorLoading(false);
    }
  }, [symbol, signalTimeframe, token]);

  const fetchAlerts = useCallback(async () => {
    try {
      const [subsRes, notifsRes] = await Promise.all([
        axios.get(`${API}/alerts/subscriptions`),
        axios.get(`${API}/alerts/notifications`)
      ]);
      setSubscriptions(subsRes.data?.subscriptions || []);
      setNotifications(notifsRes.data?.notifications || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const createAlert = async () => {
    try {
      const payload = {
        symbol: newAlert.symbol,
        alert_type: newAlert.alert_type,
        price_target: newAlert.price_target ? parseFloat(newAlert.price_target) : null,
        timeframe: newAlert.timeframe,
        min_confidence: newAlert.min_confidence,
        enabled: true
      };
      await axios.post(`${API}/alerts/subscribe`, payload);
      setShowAlertModal(false);
      setNewAlert({ symbol: "XAU/USD", alert_type: "SIGNAL_ANY", price_target: "", timeframe: "5min", min_confidence: 50 });
      fetchAlerts();
    } catch (e) {
      console.error(e);
    }
  };

  const deleteAlert = async (id) => {
    try {
      await axios.delete(`${API}/alerts/unsubscribe/${id}`);
      fetchAlerts();
    } catch (e) {
      console.error(e);
    }
  };

  const clearNotifications = async () => {
    try {
      await axios.delete(`${API}/alerts/notifications/clear`);
      setNotifications([]);
    } catch (e) {
      console.error(e);
    }
  };

  // Analyze button handler - fetches everything on demand
  const handleAnalyze = useCallback(async () => {
    setLoading(true);
    setDataLoaded(true);
    localStorage.setItem("ttanalysis_symbol", symbol);
    localStorage.setItem("ttanalysis_timeframe", timeframe);
    try {
      await Promise.all([fetchData(), fetchPrice(), fetchSignals(), fetchAlerts()]);
      fetchMultiIndicatorSignals();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [fetchData, fetchPrice, fetchSignals, fetchAlerts, fetchMultiIndicatorSignals, symbol, timeframe, signalMethod]);

  // When user changes symbol or timeframe, reset data so they must click Analyze again
  useEffect(() => {
    setDataLoaded(false);
    setChartData([]);
    setPrice(null);
    setBestBuy(null);
    setBestSell(null);
    setSignal(null);
    setMultiIndicatorData(null);
    setSignalMeta(null);
  }, [symbol, timeframe]);

  // Save selections to localStorage
  useEffect(() => {
    localStorage.setItem("ttanalysis_symbol", symbol);
  }, [symbol]);
  useEffect(() => {
    localStorage.setItem("ttanalysis_timeframe", timeframe);
  }, [timeframe]);

  // Fetch multi-indicator data only when data is loaded and method changes
  useEffect(() => {
    if (dataLoaded) {
      fetchMultiIndicatorSignals();
    }
  }, [signalMethod, activeTab, fetchMultiIndicatorSignals, dataLoaded]);

  const currentPair = PAIRS.find(p => p.symbol === symbol);
  const unreadNotifs = notifications.filter(n => !n.read).length;
  
  // User privileges - check what features the user can access
  const canViewSignals = user?.can_view_signals ?? true;  // Default to true for backward compat
  const canAccessTraining = user?.can_access_training ?? false;


  // Show loading screen while checking auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} />;
  }

  // Block expired memberships (except admin/supervisor/permanent users)
  if (
    membershipData &&
    membershipData.membership_active === false &&
    !["admin", "supervisor", "permanent"].includes(user?.role)
  ) {
    return (
      <MembershipLock
        user={user}
        token={token}
        lang={lang}
        onLogout={handleLogout}
        onPaymentSuccess={() => {
          axios.get(`${API}/membership-status`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => setMembershipData(r.data))
            .catch(() => {});
        }}
      />
    );
  }

  // ============== TIER GATING ==============
  // Admin panel takes precedence
  if (showAdminPanel && user?.role === "admin") {
    return (
      <AdminPanel
        token={token}
        onBack={() => setShowAdminPanel(false)}
        currentUser={user}
        onUserUpdate={(updatedUser) => {
          setUser(updatedUser);
          localStorage.setItem("user", JSON.stringify(updatedUser));
        }}
      />
    );
  }

  // Basic tier: ONLY Gold 15min Combination signal — no nav, no symbol/timeframe selectors
  // Active when basic toggle is ON and advanced is OFF
  const hasAdvanced = user?.tier_advanced || user?.signal_tier === "advanced";
  const hasBasic = user?.tier_basic || user?.signal_tier === "basic";
  if (user?.role !== "admin" && hasBasic && !hasAdvanced) {
    return (
      <BasicTierView
        user={user}
        token={token}
        lang={lang}
        toggleLanguage={toggleLanguage}
        membershipData={membershipData}
        onLogout={handleLogout}
        onPayPal={handlePayPalRenew}
      />
    );
  }

  // Render Elliott Wave / Fibonacci / Advanced / Tia Scalping full pages
  if (activePage === "elliott" || activePage === "fibonacci" || activePage === "advanced" || activePage === "tia-scalping") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-100 to-cyan-100 text-zinc-800">
        <KawaiiNav
          activePage={activePage}
          setActivePage={(p) => { setActivePage(p); setSelectedStrategyId(null); }}
          lang={lang}
          user={user}
          membershipData={membershipData}
          onLogout={handleLogout}
          onAdmin={() => setShowAdminPanel(true)}
          onPayPal={handlePayPalRenew}
        />
        <div className="p-3 sm:p-4">
          {activePage === "elliott" && <ElliottWavePage lang={lang} symbol={symbol} timeframe={timeframe} />}
          {activePage === "fibonacci" && <FibonacciPage lang={lang} symbol={symbol} timeframe={timeframe} />}
          {activePage === "advanced" && <AdvancedPage lang={lang} symbol={symbol} timeframe={timeframe} />}
          {activePage === "tia-scalping" && <TiaScalpingPage lang={lang} user={user} />}
        </div>
      </div>
    );
  }

  // Render Strategies pages (list + detail) full screen
  if (activePage === "strategies") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-100 to-cyan-100 text-zinc-800">
        <KawaiiNav
          activePage={activePage}
          setActivePage={(p) => { setActivePage(p); setSelectedStrategyId(null); }}
          lang={lang}
          user={user}
          membershipData={membershipData}
          onLogout={handleLogout}
          onAdmin={() => setShowAdminPanel(true)}
          onPayPal={handlePayPalRenew}
        />
        <div className="p-3 sm:p-4">
          {selectedStrategyId ? (
            <StrategyDetailPage
              strategyId={selectedStrategyId}
              lang={lang}
              symbol={symbol}
              timeframe={timeframe}
              educationOnly={user?.role !== "admin" && !user?.tier_advanced && !user?.tier_basic && user?.signal_tier !== "advanced" && user?.signal_tier !== "basic"}
              onBack={() => setSelectedStrategyId(null)}
            />
          ) : (
            <StrategiesListPage lang={lang} onSelect={(id) => setSelectedStrategyId(id)} />
          )}
        </div>
      </div>
    );
  }

  // Render Morning Star full page
  if (activePage === "morningstar" && chartData.length > 0) {
    return (
      <MorningStarPage
        chartData={chartData}
        signal={bestBuy || bestSell}
        multiIndicatorData={multiIndicatorData}
        symbol={symbol}
        timeframe={timeframe}
        lang={lang}
        onBack={() => setActivePage("signals")}
      />
    );
  }


  // Render Paper Trading view if active
  if (showPaperTrading && canAccessTraining) {
    return (
      <PaperTrading 
        token={token} 
        onBack={() => setShowPaperTrading(false)} 
        user={user}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-100 to-cyan-100 text-zinc-800">
      {/* Kawaii Navigation */}
      <KawaiiNav
        activePage={activePage}
        setActivePage={setActivePage}
        lang={lang}
        user={user}
        membershipData={membershipData}
        onLogout={handleLogout}
        onAdmin={() => setShowAdminPanel(true)}
        onPayPal={handlePayPalRenew}
      />

      <div className="p-2 sm:p-4">
      {/* Header - just controls (logo is in KawaiiNav) */}
      <div className="flex items-center justify-end mb-4 sm:mb-6">
        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3 w-full">
          <Select value={symbol} onValueChange={setSymbol}>
            <SelectTrigger className="w-32 sm:w-44 h-9 text-xs sm:text-sm bg-white border-pink-300" data-testid="symbol-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-pink-200 max-h-80">
              <div className="px-2 py-1 text-xs text-pink-500 font-semibold">Forex</div>
              {PAIRS.filter(p => p.category === "Forex").map((p) => (
                <SelectItem key={p.symbol} value={p.symbol}>{p.name}</SelectItem>
              ))}
              <div className="px-2 py-1 text-xs text-pink-500 font-semibold mt-2">Indices</div>
              {PAIRS.filter(p => p.category === "Indices").map((p) => (
                <SelectItem key={p.symbol} value={p.symbol}>{p.name}</SelectItem>
              ))}
              <div className="px-2 py-1 text-xs text-pink-500 font-semibold mt-2">Commodities</div>
              {PAIRS.filter(p => p.category === "Commodities").map((p) => (
                <SelectItem key={p.symbol} value={p.symbol}>{p.name}</SelectItem>
              ))}
              <div className="px-2 py-1 text-xs text-pink-500 font-semibold mt-2">Crypto</div>
              {PAIRS.filter(p => p.category === "Crypto").map((p) => (
                <SelectItem key={p.symbol} value={p.symbol}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={timeframe} onValueChange={setTimeframe}>
            <SelectTrigger className="w-20 sm:w-24 h-9 text-xs sm:text-sm bg-white border-pink-300" data-testid="timeframe-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-pink-200">
              {TIMEFRAMES.map((tf) => (
                <SelectItem key={tf} value={tf}>{tf}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button 
            onClick={handleAnalyze} 
            disabled={loading}
            className="gap-1.5 h-9 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-3 sm:px-5 text-xs sm:text-sm"
            data-testid="analyze-btn"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {t.analyze || "Analyze"}
          </Button>
          
          {dataLoaded && (
            <Button 
              variant="outline"
              size="icon"
              onClick={() => { setDataLoaded(false); setChartData([]); setPrice(null); setBestBuy(null); setBestSell(null); setSignal(null); setMultiIndicatorData(null); setSignalMeta(null); }}
              className="h-9 w-9 border-red-300 text-red-500 hover:bg-red-50"
              data-testid="exit-market-btn"
              title={lang === "ar" ? "خروج من السوق" : "Exit Market"}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
          
          {/* Language Toggle */}
          <Button 
            variant="outline" 
            size="sm"
            onClick={toggleLanguage}
            className="gap-1.5 h-9 min-w-[60px] sm:min-w-[80px] text-xs sm:text-sm border-pink-300 text-pink-700 bg-white"
            data-testid="language-toggle"
          >
            <Languages className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            {lang === "en" ? "عربي" : "EN"}
          </Button>
          
          <Button 
            variant="outline" 
            size="icon" 
            className="relative h-9 w-9 border-pink-300 text-pink-700 bg-white"
            onClick={() => setActiveTab("alerts")}
            data-testid="alerts-btn"
          >
            {unreadNotifs > 0 ? <BellRing className="w-4 h-4 text-yellow-500" /> : <Bell className="w-4 h-4" />}
            {unreadNotifs > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-xs rounded-full w-5 h-5 flex items-center justify-center text-white">
                {unreadNotifs}
              </span>
            )}
          </Button>

          {/* Paper Trading Button - Only for users with training access */}
          {canAccessTraining && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowPaperTrading(true)}
              className="gap-1.5 h-9 border-blue-400 text-blue-600 bg-white text-xs sm:text-sm"
              data-testid="paper-trading-btn"
            >
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">{t.paperTrading}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Signal Controls: Timeframe + Method Selector */}
      {!dataLoaded ? (
        /* Empty state - waiting for user to click Analyze */
        <div className="flex flex-col items-center justify-center py-16 sm:py-32" data-testid="select-market-prompt">
          <div className="bg-white border-2 border-pink-200 rounded-2xl p-5 sm:p-10 max-w-lg w-full text-center shadow-lg shadow-pink-200/40">
            <Search className="w-12 h-12 sm:w-14 sm:h-14 text-emerald-500 mx-auto mb-4 sm:mb-5" />
            <h2 className="text-xl sm:text-2xl font-bold text-pink-600 mb-2 sm:mb-3">{t.selectMarketPrompt}</h2>
            <p className="text-zinc-600 mb-6 sm:mb-8 text-sm leading-relaxed">{t.selectMarketDesc}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6">
              <Select value={symbol} onValueChange={setSymbol}>
                <SelectTrigger className="w-48 bg-zinc-800 border-zinc-600" data-testid="prompt-symbol-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 max-h-80">
                  <div className="px-2 py-1 text-xs text-zinc-500 font-semibold">Forex</div>
                  {PAIRS.filter(p => p.category === "Forex").map((p) => (
                    <SelectItem key={p.symbol} value={p.symbol}>{p.name}</SelectItem>
                  ))}
                  <div className="px-2 py-1 text-xs text-zinc-500 font-semibold mt-2">Commodities</div>
                  {PAIRS.filter(p => p.category === "Commodities").map((p) => (
                    <SelectItem key={p.symbol} value={p.symbol}>{p.name}</SelectItem>
                  ))}
                  <div className="px-2 py-1 text-xs text-zinc-500 font-semibold mt-2">Crypto</div>
                  {PAIRS.filter(p => p.category === "Crypto").map((p) => (
                    <SelectItem key={p.symbol} value={p.symbol}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={timeframe} onValueChange={setTimeframe}>
                <SelectTrigger className="w-28 bg-zinc-800 border-zinc-600" data-testid="prompt-timeframe-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  {TIMEFRAMES.map((tf) => (
                    <SelectItem key={tf} value={tf}>{tf}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={handleAnalyze}
              disabled={loading}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-3 text-base"
              data-testid="prompt-analyze-btn"
            >
              {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              {t.analyze}
            </Button>
          </div>
        </div>
      ) : (
      <>
      {/* Per-user analyze rate-limit notice (3 min) */}
      {goldRateError && (
        <Card className="bg-yellow-950/40 border-2 border-yellow-500/50 mb-4" data-testid="gold-rate-error">
          <CardContent className="p-3 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0" />
            <p className="text-yellow-200 text-sm">
              {lang === "ar"
                ? "تحليل واحد كل 3 دقائق لكل مستخدم. الرجاء الانتظار."
                : "Only 1 analysis every 3 minutes per user. Please wait."}
              <span className="ml-2 text-yellow-400 font-mono">{goldRateError}</span>
            </p>
          </CardContent>
        </Card>
      )}

      {/* Live Price Banner - TOP OF PAGE */}
      <Card className="bg-white border-pink-200 mb-4" data-testid="live-price-banner">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="w-full sm:w-auto">
              <span className="text-zinc-600 text-xs sm:text-sm">{currentPair?.name || symbol} <Badge variant="outline" className="ml-2 text-[10px] sm:text-xs border-pink-300 text-pink-600">{currentPair?.category}</Badge></span>
              <p className="text-3xl sm:text-5xl font-bold font-mono text-pink-600 break-all" data-testid="current-price">
                {price ? price.toFixed(5) : multiIndicatorData?.current_price ? multiIndicatorData.current_price.toFixed(5) : signal?.entry_price ? Number(signal.entry_price).toFixed(5) : "Loading..."}
              </p>
            </div>
            {signal && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 w-full sm:w-auto">
                <div className="text-center">
                  <Badge className={`text-sm sm:text-lg px-3 sm:px-4 py-1.5 sm:py-2 ${signal.signal_type === "BUY" ? "bg-green-600" : signal.signal_type === "SELL" ? "bg-red-600" : "bg-zinc-600"}`}>
                    {signal.signal_type === "BUY" && <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5" />}
                    {signal.signal_type === "SELL" && <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5" />}
                    {signal.signal_type}
                  </Badge>
                  <p className="text-xs sm:text-sm text-zinc-600 mt-1">{signal.confidence}% ({timeframe})</p>
                </div>
                <div className="text-right space-y-0.5 sm:space-y-1 text-xs sm:text-sm">
                  <div><span className="text-zinc-500">{t.entry}:</span> <span className="font-mono text-zinc-800">{signal.entry_price}</span></div>
                  <div><span className="text-zinc-500">{t.stopLoss}:</span> <span className="font-mono text-red-500">{signal.stop_loss}</span></div>
                  <div><span className="text-zinc-500">{t.takeProfit}:</span> <span className="font-mono text-green-600">{signal.take_profit_1}</span></div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Economic Calendar & Market Sessions */}
      <EconomicCalendarWidget lang={lang} />

      {/* Signal Controls: Timeframe + Method Selector */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-base sm:text-lg font-semibold text-pink-600">Trading Signals</h2>
          {signalMeta && signalMethod === "combined" && (
            <Badge className={`${
              signalMeta.overall_trend === "BULLISH" ? "bg-green-600" :
              signalMeta.overall_trend === "BEARISH" ? "bg-red-600" : "bg-yellow-600"
            }`}>
              {signalMeta.overall_trend}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <span className="text-xs sm:text-sm text-zinc-600 shrink-0">{t.method}:</span>
          <Select value={signalMethod} onValueChange={setSignalMethod}>
            <SelectTrigger className="flex-1 sm:w-44 h-9 text-xs sm:text-sm bg-white border-purple-300 text-purple-600" data-testid="signal-method-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-purple-200">
              {SIGNAL_METHODS.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {lang === "ar" ? m.labelAr : m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Method Description */}
      {signalMethod !== "combined" && (
        <div className="mb-4 p-3 bg-purple-900/20 border border-purple-500/30 rounded-lg">
          <p className="text-purple-300 text-sm">
            <span className="font-semibold">{SIGNAL_METHODS.find(m => m.value === signalMethod)?.labelAr}</span>
            {" - "}
            {t.showingSignals} {SIGNAL_METHODS.find(m => m.value === signalMethod)?.label} {t.indicatorOnly}
          </p>
        </div>
      )}

      {/* BUY & SELL Signals Side by Side - Only show if user has signal access */}
      {canViewSignals ? (
        signalMethod === "combined" ? (
          // Original combined signals + All indicators summary grid
          <>
            {activePage === "signals" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <SignalCard sig={bestBuy} title={`Best BUY (${signalTimeframe})`} type="BUY" multiIndicatorData={multiIndicatorData} lang={lang} />
                <SignalCard sig={bestSell} title={`Best SELL (${signalTimeframe})`} type="SELL" multiIndicatorData={multiIndicatorData} lang={lang} />
              </div>
            )}
            
            {/* 19 Indicators Summary Grid */}
            {activePage === "indicators" && multiIndicatorData && multiIndicatorData.indicators && (
              <Card className="bg-zinc-900 border-zinc-800 mb-6">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-zinc-300 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    {lang === "ar" ? "ملخص 19 مؤشر تقني + أخبار" : "19 Technical Indicators + News Summary"}
                  </CardTitle>
                  {multiIndicatorData.summary && (
                    <div className="flex items-center gap-3 mt-2">
                      <Badge className={`${multiIndicatorData.summary.overall_recommendation === "BUY" ? "bg-green-600" : multiIndicatorData.summary.overall_recommendation === "SELL" ? "bg-red-600" : "bg-zinc-600"}`}>
                        {multiIndicatorData.summary.overall_recommendation}
                      </Badge>
                      <span className="text-sm text-zinc-400">
                        {lang === "ar" ? "الثقة" : "Confidence"}: {multiIndicatorData.summary.overall_confidence?.toFixed(1)}%
                      </span>
                      <span className="text-sm">
                        <span className="text-green-400">{multiIndicatorData.summary.total_buy_signals} {lang === "ar" ? "شراء" : "BUY"}</span>
                        {" / "}
                        <span className="text-red-400">{multiIndicatorData.summary.total_sell_signals} {lang === "ar" ? "بيع" : "SELL"}</span>
                        {" / "}
                        <span className="text-zinc-400">{multiIndicatorData.summary.total_neutral_signals} {lang === "ar" ? "محايد" : "NEUTRAL"}</span>
                      </span>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    {SIGNAL_METHODS.filter(m => m.value !== "combined").map((m) => {
                      const ind = multiIndicatorData.indicators[m.value];
                      if (!ind) return null;
                      const isBuyInd = ind.signal_type === "BUY";
                      const isSellInd = ind.signal_type === "SELL";
                      return (
                        <button
                          key={m.value}
                          onClick={() => setSignalMethod(m.value)}
                          className={`p-2 rounded border text-left transition-all hover:scale-105 cursor-pointer ${
                            isBuyInd ? "border-green-700 bg-green-950/40" : 
                            isSellInd ? "border-red-700 bg-red-950/40" : 
                            "border-zinc-700 bg-zinc-800/50"
                          }`}
                          data-testid={`indicator-${m.value}`}
                        >
                          <p className="text-xs text-zinc-400 truncate">{lang === "ar" ? m.labelAr : m.label}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <span className={`text-sm font-bold ${isBuyInd ? "text-green-400" : isSellInd ? "text-red-400" : "text-zinc-400"}`}>
                              {ind.signal_type}
                            </span>
                            <span className={`text-xs ${ind.strength === "STRONG" ? "text-yellow-400" : ind.strength === "MODERATE" ? "text-zinc-300" : "text-zinc-500"}`}>
                              {ind.strength === "STRONG" ? "***" : ind.strength === "MODERATE" ? "**" : "*"}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {/* Live News Headlines + Economic Events */}
                  {multiIndicatorData.market_context && (
                    <div className="mt-4 pt-3 border-t border-zinc-800">
                      {multiIndicatorData.market_context.headlines && multiIndicatorData.market_context.headlines.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-semibold text-zinc-300 mb-2 flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            {lang === "ar" ? "أخبار مباشرة" : "Live News"} 
                            <Badge className={`ml-2 text-xs ${multiIndicatorData.market_context.news_sentiment === "BULLISH" ? "bg-green-700" : multiIndicatorData.market_context.news_sentiment === "BEARISH" ? "bg-red-700" : "bg-zinc-700"}`}>
                              {multiIndicatorData.market_context.news_sentiment} ({multiIndicatorData.market_context.sentiment_score > 0 ? "+" : ""}{multiIndicatorData.market_context.sentiment_score?.toFixed(2)})
                            </Badge>
                          </p>
                          <div className="space-y-1">
                            {multiIndicatorData.market_context.headlines.slice(0, 5).map((headline, i) => (
                              <p key={i} className="text-xs text-zinc-400 flex items-start gap-1">
                                <span className="text-blue-400 mt-0.5 shrink-0">&#8226;</span>
                                <span className="line-clamp-2">{headline}</span>
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                      {multiIndicatorData.market_context.economic_events && multiIndicatorData.market_context.economic_events.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-yellow-400 mb-2 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {lang === "ar" ? "أحداث اقتصادية مهمة" : "High Impact Economic Events"}
                          </p>
                          <div className="space-y-1">
                            {multiIndicatorData.market_context.economic_events.map((event, i) => (
                              <p key={i} className="text-xs text-yellow-300/70 flex items-start gap-1">
                                <span className="text-yellow-500 mt-0.5 shrink-0">&#9888;</span>
                                <span>{event}</span>
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            {/* Strategy Analysis */}
            {activePage === "indicators" && multiIndicatorData?.strategies && (
              <StrategyAnalysis strategies={multiIndicatorData.strategies} lang={lang} />
            )}
          </>
        ) : (
          // Per-Indicator Signal View
          activePage === "indicators" ? (
            <MethodSignalView 
              data={multiIndicatorData} 
              method={signalMethod} 
              timeframe={signalTimeframe}
              loading={multiIndicatorLoading}
              symbol={symbol}
              lang={lang}
            />
          ) : null
        )
      ) : (
        <Card className="bg-zinc-900 border-zinc-700 mb-6">
          <CardContent className="p-8 text-center">
            <TrendingUp className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-zinc-400">Trading Signals</h3>
            <p className="text-sm text-zinc-500 mt-2">
              You do not have access to trading signals. Contact admin to upgrade your account.
            </p>
          </CardContent>
        </Card>
      )}


      <div className="grid grid-cols-12 gap-4">
        {/* Main Content */}
        <div className="col-span-12 lg:col-span-9">
          {activePage === "signals" && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-zinc-900 border border-zinc-800">
              <TabsTrigger value="chart"><BarChart3 className="w-4 h-4 mr-2" />{t.chart}</TabsTrigger>
              <TabsTrigger value="signals"><Zap className="w-4 h-4 mr-2" />{t.signals}</TabsTrigger>
              <TabsTrigger value="alerts" className="relative">
                <Bell className="w-4 h-4 mr-2" />{t.alerts}
                {unreadNotifs > 0 && <span className="ml-1 bg-red-500 text-xs rounded-full px-1.5">{unreadNotifs}</span>}
              </TabsTrigger>
              {canAccessTraining && (
                <TabsTrigger value="education"><GraduationCap className="w-4 h-4 mr-2" />{t.training}</TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="chart">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle>{symbol} - {timeframe}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    {/* Real-time price display on the side */}
                    <div className="absolute right-4 top-4 z-10 bg-zinc-950/90 backdrop-blur-sm border border-pink-500/50 rounded-lg p-4 shadow-lg shadow-pink-500/20">
                      <p className="text-xs text-pink-300 uppercase tracking-wider mb-1">Live Price</p>
                      <p className="text-3xl font-bold font-mono text-pink-500 animate-pulse" data-testid="live-price">
                        {price ? price.toFixed(symbol.includes("JPY") ? 3 : 5) : "---"}
                      </p>
                      <p className="text-xs text-pink-400 mt-1">{currentPair?.name}</p>
                    </div>
                    
                    <div className="h-80" data-testid="price-chart">
                    {chartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis dataKey="datetime" tick={{ fill: '#888', fontSize: 10 }} tickFormatter={(v) => v?.split(' ')[1]?.slice(0,5) || ''} />
                          <YAxis domain={['auto', 'auto']} tick={{ fill: '#888', fontSize: 10 }} />
                          <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }} />
                          <Line type="monotone" dataKey="close" stroke="#10b981" strokeWidth={2} dot={false} name="Price" />
                          <Line type="monotone" dataKey="sma_20" stroke="#3b82f6" strokeWidth={1} dot={false} name="SMA20" />
                          <Line type="monotone" dataKey="bb_upper" stroke="#8b5cf6" strokeWidth={1} dot={false} strokeDasharray="3 3" />
                          <Line type="monotone" dataKey="bb_lower" stroke="#8b5cf6" strokeWidth={1} dot={false} strokeDasharray="3 3" />
                          {levels?.r1 && <ReferenceLine y={levels.r1} stroke="#ef4444" strokeDasharray="5 5" />}
                          {levels?.s1 && <ReferenceLine y={levels.s1} stroke="#22c55e" strokeDasharray="5 5" />}
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <RefreshCw className="w-8 h-8 animate-spin text-zinc-600" />
                      </div>
                    )}
                    </div>
                  </div>

                  <div className="h-20 mt-4">
                    <p className="text-xs text-zinc-500 mb-1">RSI (14)</p>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <YAxis domain={[0, 100]} tick={{ fill: '#888', fontSize: 8 }} ticks={[30, 70]} />
                        <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="3 3" />
                        <ReferenceLine y={30} stroke="#22c55e" strokeDasharray="3 3" />
                        <Line type="monotone" dataKey="rsi" stroke="#8b5cf6" strokeWidth={1.5} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="signals">
              {canViewSignals ? (
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold">Best Opportunities for {symbol}</h2>
                  <div className="grid gap-4 md:grid-cols-2">
                    <SignalCard sig={bestBuy} title="Best BUY" type="BUY" multiIndicatorData={multiIndicatorData} lang={lang} />
                    <SignalCard sig={bestSell} title="Best SELL" type="SELL" multiIndicatorData={multiIndicatorData} lang={lang} />
                  </div>
                </div>
              ) : (
                <Card className="bg-zinc-900 border-zinc-700">
                  <CardContent className="p-8 text-center">
                    <TrendingUp className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-zinc-400">Trading Signals</h3>
                    <p className="text-sm text-zinc-500 mt-2">
                      You do not have access to trading signals. Contact admin to upgrade your account.
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="alerts">
              <div className="space-y-4">
                {/* Add Alert Button */}
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold">Alert Subscriptions</h2>
                  <Button onClick={() => setShowAlertModal(true)} data-testid="add-alert-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Alert
                  </Button>
                </div>

                {/* Alert Modal */}
                {showAlertModal && (
                  <Card className="bg-zinc-900 border-zinc-700">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle>Create New Alert</CardTitle>
                      <Button variant="ghost" size="icon" onClick={() => setShowAlertModal(false)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-zinc-400 block mb-1">Symbol</label>
                          <Select value={newAlert.symbol} onValueChange={(v) => setNewAlert({...newAlert, symbol: v})}>
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              {PAIRS.map((p) => (
                                <SelectItem key={p.symbol} value={p.symbol}>{p.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm text-zinc-400 block mb-1">Timeframe</label>
                          <Select value={newAlert.timeframe} onValueChange={(v) => setNewAlert({...newAlert, timeframe: v})}>
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              {TIMEFRAMES.map((tf) => (
                                <SelectItem key={tf} value={tf}>{tf}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-sm text-zinc-400 block mb-1">Alert Type</label>
                        <Select value={newAlert.alert_type} onValueChange={(v) => setNewAlert({...newAlert, alert_type: v})}>
                          <SelectTrigger className="bg-zinc-800 border-zinc-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            {ALERT_TYPES.map((t) => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {newAlert.alert_type.startsWith("SIGNAL") && (
                        <div>
                          <label className="text-sm text-zinc-400 block mb-2">
                            Minimum Confidence: <span className="text-white font-bold">{newAlert.min_confidence}%</span>
                          </label>
                          <Slider
                            value={[newAlert.min_confidence]}
                            onValueChange={(v) => setNewAlert({...newAlert, min_confidence: v[0]})}
                            min={20}
                            max={90}
                            step={5}
                            className="w-full"
                          />
                          <div className="flex justify-between text-xs text-zinc-500 mt-1">
                            <span>20%</span>
                            <span>50%</span>
                            <span>90%</span>
                          </div>
                        </div>
                      )}
                      
                      {(newAlert.alert_type === "PRICE_ABOVE" || newAlert.alert_type === "PRICE_BELOW") && (
                        <div>
                          <label className="text-sm text-zinc-400 block mb-1">Target Price</label>
                          <Input 
                            type="number" 
                            step="0.00001"
                            value={newAlert.price_target}
                            onChange={(e) => setNewAlert({...newAlert, price_target: e.target.value})}
                            className="bg-zinc-800 border-zinc-700"
                            placeholder="Enter target price"
                          />
                        </div>
                      )}
                      
                      <div className="bg-zinc-800/50 p-3 rounded text-sm">
                        <p className="text-zinc-400">Alert Preview:</p>
                        <p className="text-white mt-1">
                          {newAlert.alert_type.startsWith("SIGNAL") 
                            ? `Notify me when ${newAlert.symbol} has a ${newAlert.alert_type.replace("SIGNAL_", "")} signal on ${newAlert.timeframe} with ≥${newAlert.min_confidence}% confidence`
                            : `Notify me when ${newAlert.symbol} price goes ${newAlert.alert_type.replace("PRICE_", "").toLowerCase()} ${newAlert.price_target || "..."}`
                          }
                        </p>
                      </div>
                      
                      <Button onClick={createAlert} className="w-full">Create Alert</Button>
                    </CardContent>
                  </Card>
                )}

                {/* Subscriptions List */}
                <div className="grid gap-3 md:grid-cols-2">
                  {subscriptions.map((sub) => (
                    <Card key={sub.id} className="bg-zinc-900 border-zinc-800">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold text-lg">{sub.symbol}</p>
                            <p className="text-sm text-zinc-400">
                              {ALERT_TYPES.find(t => t.value === sub.alert_type)?.label}
                            </p>
                            <div className="flex gap-2 mt-2">
                              <Badge variant="outline" className="text-xs">{sub.timeframe}</Badge>
                              {sub.min_confidence && (
                                <Badge variant="outline" className="text-xs bg-blue-500/20 text-blue-400">
                                  ≥{sub.min_confidence}% conf
                                </Badge>
                              )}
                              {sub.price_target && (
                                <Badge variant="outline" className="text-xs">@ {sub.price_target}</Badge>
                              )}
                            </div>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => deleteAlert(sub.id)}>
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {subscriptions.length === 0 && (
                    <p className="text-zinc-500 col-span-2 text-center py-4">No alerts configured. Click Add Alert to create one.</p>
                  )}
                </div>

                {/* Notifications */}
                <div className="flex justify-between items-center mt-6">
                  <h2 className="text-lg font-semibold">Notifications ({notifications.length})</h2>
                  {notifications.length > 0 && (
                    <Button variant="outline" size="sm" onClick={clearNotifications}>
                      <Trash2 className="w-4 h-4 mr-2" /> Clear All
                    </Button>
                  )}
                </div>
                <ScrollArea className="h-64">
                  <div className="space-y-2">
                    {notifications.slice().reverse().map((notif) => (
                      <Card key={notif.id} className={`bg-zinc-900 border-l-4 ${notif.signal_type === "BUY" ? "border-l-green-500" : notif.signal_type === "SELL" ? "border-l-red-500" : "border-l-blue-500"}`}>
                        <CardContent className="p-3">
                          <p className="font-medium">{notif.message}</p>
                          {notif.entry_price && (
                            <div className="flex gap-4 text-sm mt-2">
                              <span>Entry: <span className="font-mono">{notif.entry_price}</span></span>
                              <span>SL: <span className="font-mono text-red-400">{notif.stop_loss}</span></span>
                              <span>TP: <span className="font-mono text-green-400">{notif.take_profit}</span></span>
                            </div>
                          )}
                          <p className="text-xs text-zinc-500 mt-1">{new Date(notif.timestamp).toLocaleString()}</p>
                        </CardContent>
                      </Card>
                    ))}
                    {notifications.length === 0 && (
                      <p className="text-zinc-500 text-center py-4">No notifications yet. Alerts will appear here.</p>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </TabsContent>

            <TabsContent value="education">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="w-6 h-6 text-blue-400" />
                    Education - Technical Indicators
                  </CardTitle>
                  <p className="text-zinc-400 text-sm">Learn how the indicators used in signal analysis work</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <EducationCard
                    title="RSI - Relative Strength Index"
                    icon="📊"
                    description="Measures the speed and change of price movements. Ranges from 0 to 100."
                    howItWorks={[
                      "RSI above 70 = Overbought zone (potential bearish reversal)",
                      "RSI below 30 = Oversold zone (potential bullish reversal)",
                      "RSI between 30-70 = Neutral zone"
                    ]}
                    signals={[
                      { type: "BUY", condition: "RSI rises from below 30" },
                      { type: "SELL", condition: "RSI drops from above 70" }
                    ]}
                    color="purple"
                  />
                  
                  <EducationCard
                    title="MACD - Moving Average Convergence Divergence"
                    icon="📈"
                    description="Tracks the relationship between two moving averages to identify trend direction and strength."
                    howItWorks={[
                      "MACD Line = EMA(12) - EMA(26)",
                      "Signal Line = EMA(9) of MACD line",
                      "Histogram = Difference between the two lines"
                    ]}
                    signals={[
                      { type: "BUY", condition: "MACD crosses Signal Line upward" },
                      { type: "SELL", condition: "MACD crosses Signal Line downward" }
                    ]}
                    color="green"
                  />
                  
                  <EducationCard
                    title="Bollinger Bands"
                    icon="📉"
                    description="Measures volatility and identifies dynamic support and resistance levels."
                    howItWorks={[
                      "Middle Band = SMA(20)",
                      "Upper Band = SMA + (2 × Standard Deviation)",
                      "Lower Band = SMA - (2 × Standard Deviation)"
                    ]}
                    signals={[
                      { type: "BUY", condition: "Price touches lower band" },
                      { type: "SELL", condition: "Price touches upper band" }
                    ]}
                    color="blue"
                  />
                  
                  <EducationCard
                    title="SMA & EMA - Moving Averages"
                    icon="〰️"
                    description="Smooths price data to identify trend direction."
                    howItWorks={[
                      "SMA = Simple average (all periods weighted equally)",
                      "EMA = Exponential average (recent periods weighted more)",
                      "Moving average crossovers give strong signals"
                    ]}
                    signals={[
                      { type: "BUY", condition: "Short EMA crosses long EMA upward (Golden Cross)" },
                      { type: "SELL", condition: "Short EMA crosses long EMA downward (Death Cross)" }
                    ]}
                    color="yellow"
                  />
                  
                  <EducationCard
                    title="ATR - Average True Range"
                    icon="📏"
                    description="Measures market volatility to set stop loss and take profit levels."
                    howItWorks={[
                      "Calculates average range between high and low prices",
                      "High ATR = High volatility",
                      "Low ATR = Quiet market"
                    ]}
                    signals={[
                      { type: "INFO", condition: "Stop Loss = Entry ± (1.5 × ATR)" },
                      { type: "INFO", condition: "Take Profit = Entry ± (2-3 × ATR)" }
                    ]}
                    color="orange"
                  />
                  
                  <EducationCard
                    title="Support & Resistance"
                    icon="🎯"
                    description="Historical price levels where price tends to bounce."
                    howItWorks={[
                      "Support = Level that prevents price from falling further",
                      "Resistance = Level that prevents price from rising further",
                      "Pivot Points calculated from (High + Low + Close) / 3"
                    ]}
                    signals={[
                      { type: "BUY", condition: "Price bounces from support level" },
                      { type: "SELL", condition: "Price bounces from resistance level" }
                    ]}
                    color="cyan"
                  />
                  
                  {/* Flash Cards Component */}
                  <div className="mt-6 pt-6 border-t border-zinc-700">
                    <FlashCards lang={lang} />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          )}
        </div>

        {/* Sidebar */}
        <div className="col-span-12 lg:col-span-3 space-y-4">
          {/* Indicators */}
          {indicators && (
            <Card className="bg-zinc-900 border-zinc-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-white">Technical Indicators</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-zinc-800 p-2 rounded border border-zinc-700">
                    <p className="text-zinc-400 text-xs">RSI</p>
                    <p className={`font-mono text-lg font-bold ${indicators.rsi_14 > 70 ? "text-red-400" : indicators.rsi_14 < 30 ? "text-green-400" : "text-white"}`}>
                      {indicators.rsi_14?.toFixed(1) || "-"}
                    </p>
                  </div>
                  <div className="bg-zinc-800 p-2 rounded border border-zinc-700">
                    <p className="text-zinc-400 text-xs">MACD</p>
                    <p className={`font-mono text-lg font-bold ${indicators.macd > 0 ? "text-green-400" : "text-red-400"}`}>
                      {indicators.macd?.toFixed(5) || "-"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Support & Resistance */}
          {levels && (
            <Card className="bg-zinc-900 border-zinc-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-white">Support & Resistance</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                <div className="flex justify-between p-1"><span className="text-red-400 font-medium">R1</span><span className="font-mono text-white">{levels.r1?.toFixed(4)}</span></div>
                <div className="flex justify-between bg-blue-900/30 p-1 rounded"><span className="text-blue-400 font-medium">Pivot</span><span className="font-mono text-white">{levels.pivot_point?.toFixed(4)}</span></div>
                <div className="flex justify-between p-1"><span className="text-green-400 font-medium">S1</span><span className="font-mono text-white">{levels.s1?.toFixed(4)}</span></div>
              </CardContent>
            </Card>
          )}

          {/* Quick Pairs */}
          <Card className="bg-zinc-900 border-zinc-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-white">Quick Select</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-40">
                <div className="space-y-1">
                  {PAIRS.slice(0, 8).map((p) => (
                    <div 
                      key={p.symbol}
                      className={`flex justify-between items-center p-2 rounded cursor-pointer transition-colors ${
                        p.symbol === symbol ? "bg-blue-600/30 border border-blue-500" : "hover:bg-zinc-800 border border-transparent"
                      }`}
                      onClick={() => setSymbol(p.symbol)}
                    >
                      <span className="text-sm text-white">{p.name}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      <footer className="text-center text-zinc-600 text-sm mt-8 py-4 border-t border-zinc-800">
        TTANALYSIS - Smart Trading Analysis Platform © 2026
      </footer>
      </>
      )}
      </div>
    </div>
  );
}

export default App;
